// Paso 1 y 2 del original: elegir empresa y tarjeta, y desde ahí cargar una
// conciliación nueva o abrir una existente.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useSesion } from '../sesion';
import type { Conciliacion, Empresa, ResultadoCarga } from '../tipos';

export function Inicio() {
  const { soloLectura } = useSesion();
  const qc = useQueryClient();
  const [empresaId, setEmpresaId] = useState('');
  const [tarjetaId, setTarjetaId] = useState('');
  const [archivos, setArchivos] = useState<File[]>([]);
  const [resultado, setResultado] = useState<ResultadoCarga | null>(null);
  const [error, setError] = useState('');

  const empresas = useQuery({
    queryKey: ['empresas'],
    queryFn: () => api.get<Empresa[]>('/api/empresas'),
  });

  const conciliaciones = useQuery({
    queryKey: ['conciliaciones', tarjetaId],
    queryFn: () => api.get<Conciliacion[]>(
      tarjetaId ? `/api/conciliaciones?tarjetaId=${tarjetaId}` : '/api/conciliaciones'),
  });

  const cargar = useMutation({
    mutationFn: (datos: FormData) => api.subir<ResultadoCarga>('/api/conciliaciones', datos),
    onSuccess: (r) => {
      setResultado(r);
      setError('');
      setArchivos([]);
      void qc.invalidateQueries({ queryKey: ['conciliaciones'] });
    },
    onError: (e) => { setError(e instanceof Error ? e.message : 'Falló la carga.'); setResultado(null); },
  });

  const empresa = empresas.data?.find((e) => e.id === empresaId);
  const pdfs = archivos.filter((f) => /\.pdf$/i.test(f.name));
  const excels = archivos.filter((f) => /\.xlsx?$/i.test(f.name));

  function procesar() {
    const fd = new FormData();
    fd.append('tarjetaId', tarjetaId);
    for (const f of archivos) fd.append('archivo', f);
    cargar.mutate(fd);
  }

  return (
    <>
      <div className="controls">
        <div>
          <label htmlFor="emp">Empresa</label>
          <select id="emp" value={empresaId} onChange={(e) => { setEmpresaId(e.target.value); setTarjetaId(''); }}>
            <option value="">— elegí una empresa —</option>
            {empresas.data?.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="tar">Tarjeta</label>
          <select id="tar" value={tarjetaId} disabled={!empresa}
                  onChange={(e) => setTarjetaId(e.target.value)}>
            <option value="">{empresa ? '— elegí una tarjeta —' : '—'}</option>
            {empresa?.tarjetas.map((t) => (
              <option key={t.id} value={t.id}>{t.nombre} ({t.cuentaContable})</option>
            ))}
          </select>
        </div>
      </div>

      {empresa && !empresa.tarjetas.length && (
        <div className="aviso warn">
          <b>{empresa.nombre}</b> no tiene tarjetas dadas de alta. Se agregan desde Administración.
        </div>
      )}

      {tarjetaId && !soloLectura && (
        <>
          <h2 className="seccion">
            <span className="num">1</span>CARGAR DOCUMENTACIÓN
            <span className="muted">— el PDF del resumen y los mayores en Excel</span>
          </h2>
          <div className="kb">
            <div className="pad">
              <p className="muted" style={{ marginTop: 0 }}>
                Elegí el resumen del emisor (PDF) y el o los mayores contables (Excel). Podés
                seleccionarlos todos juntos. Para American Express podés elegir los siete PDF de
                los titulares. Si volvés a cargar la misma tarjeta y período, <b>reemplaza</b> la
                conciliación anterior.
              </p>
              <input type="file" multiple accept=".pdf,.xlsx,.xls"
                     onChange={(e) => { setArchivos([...(e.target.files ?? [])]); setResultado(null); setError(''); }} />
              {archivos.length > 0 && (
                <p className="muted">
                  Seleccionados: <b>{pdfs.length}</b> PDF y <b>{excels.length}</b> Excel
                  {' — '}{archivos.map((f) => f.name).join(' · ')}
                </p>
              )}
              <button className="btn-run" style={{ marginTop: 8 }}
                      disabled={!archivos.length || cargar.isPending}
                      onClick={procesar}>
                {cargar.isPending ? 'Procesando…' : 'Procesar y conciliar'}
              </button>
              {cargar.isPending && (
                <p className="muted">Leyendo el PDF y cruzando contra el mayor. Puede tardar unos segundos.</p>
              )}
              {error && <div className="aviso bad" role="alert">{error}</div>}
              {resultado && (
                <div className="aviso ok" role="status">
                  <b>{resultado.banco}</b> · período {resultado.periodo} — leídos{' '}
                  {resultado.consumos} consumos, {resultado.cargos} cargos y{' '}
                  {resultado.movimientos} movimientos del mayor.
                  <br />
                  Conciliados automáticamente <b>{resultado.conciliacion.conciliados}</b> de{' '}
                  {resultado.conciliacion.total}
                  {resultado.conciliacion.agrupaciones > 0 &&
                    ` · ${resultado.conciliacion.agrupaciones} agrupación(es)`}
                  {resultado.conciliacion.porReglaAprendida > 0 &&
                    ` · ${resultado.conciliacion.porReglaAprendida} por regla aprendida`}
                  {' — '}
                  <Link to={`/conciliacion/${resultado.conciliacionId}`}>abrir la conciliación</Link>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <h2 className="seccion">
        <span className="num">2</span>CONCILIACIONES
        <span className="muted">— qué está cerrado y qué tiene diferencia</span>
      </h2>
      <div className="scroll">
        <table>
          <thead>
            <tr>
              <th>Empresa</th><th>Tarjeta</th><th>Cuenta</th><th>Período</th>
              <th className="num">Tildes</th><th>Estado</th><th>Firmada por</th><th></th>
            </tr>
          </thead>
          <tbody>
            {conciliaciones.isLoading && (
              <tr><td colSpan={8} className="muted" style={{ padding: 14 }}>Cargando…</td></tr>
            )}
            {conciliaciones.data?.length === 0 && (
              <tr><td colSpan={8} className="muted" style={{ padding: 14 }}>
                No hay conciliaciones todavía. Elegí una empresa y una tarjeta y cargá el resumen.
              </td></tr>
            )}
            {conciliaciones.data?.map((c) => (
              <tr key={c.id}>
                <td>{c.tarjeta.empresa.nombre}</td>
                <td>{c.tarjeta.nombre}</td>
                <td className="cn">{c.tarjeta.cuentaContable}</td>
                <td>{c.periodo}</td>
                <td className="num">{c._count?.vinculos ?? 0}</td>
                <td>
                  <span className={`tag ${c.estado === 'cerrada' ? 'ok' : 'warn'}`}>
                    {c.estado === 'cerrada' ? 'CERRADA' : 'abierta'}
                  </span>
                </td>
                <td className="muted">
                  {c.cerradaPor?.nombre ?? '—'}
                  {c.cerradaEl && <> · {c.cerradaEl.slice(0, 10)}</>}
                </td>
                <td><Link to={`/conciliacion/${c.id}`}>abrir</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
